/**
 * Reading a prompt generation's `results` JSON: one item per generated prompt.
 *
 * The free tool and the results email group prompts by the framework that
 * wrote them. Generations stored since the free tool gained buyer personas
 * carry `framework` on each item; older ones do not, so the framework is
 * inferred from the query type (each framework's instructions pin its types,
 * except "original", which owns everything that is not another framework's).
 *
 * Pure: safe for `@repo/core/client`.
 */

import {
	PROMPT_GENERATOR_FRAMEWORKS,
	PROMPT_GENERATOR_FRAMEWORK_LABELS,
	type PromptGeneratorFramework,
} from "../types/discovery-query";

/** One stored prompt in `PromptGeneration.results`. */
export interface PromptGenerationResultItem {
	id: string;
	query: string;
	queryType: string;
	framework?: string | null;
	category?: string | null;
	useCase?: string | null;
	budget?: string | null;
	segment?: string | null;
	comparisonFocus?: string | null;
	location?: string | null;
	isLocal?: boolean | null;
}

export interface PromptGenerationFrameworkGroup<
	T extends PromptGenerationResultItem = PromptGenerationResultItem,
> {
	framework: PromptGeneratorFramework;
	label: string;
	prompts: T[];
}

function isFramework(value: unknown): value is PromptGeneratorFramework {
	return (
		typeof value === "string" &&
		(PROMPT_GENERATOR_FRAMEWORKS as readonly string[]).includes(value)
	);
}

/** The framework a query type belongs to (fallback for older generations). */
export function promptGeneratorFrameworkForQueryType(
	queryType: string,
): PromptGeneratorFramework {
	if (queryType === "category_entry_point") {
		return "category_entry_points";
	}
	if (queryType === "jobs_to_be_done") {
		return "jobs_to_be_done";
	}
	if (queryType.startsWith("buyers_journey")) {
		return "buyers_journey";
	}
	if (queryType === "stakeholder_perspective") {
		return "stakeholder_perspectives";
	}
	if (queryType === "brand_identity" || queryType === "brand_comparison") {
		return "brand_prompts";
	}
	return "original";
}

/** The stored framework when valid, else the one inferred from the type. */
export function promptGeneratorFrameworkFor(
	item: Pick<PromptGenerationResultItem, "framework" | "queryType">,
): PromptGeneratorFramework {
	return isFramework(item.framework)
		? item.framework
		: promptGeneratorFrameworkForQueryType(item.queryType);
}

/**
 * Stored `results` → items, dropping anything without a query (lenient: the
 * column is JSON and older rows vary).
 */
export function parsePromptGenerationResults(
	value: unknown,
): PromptGenerationResultItem[] {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter(
		(item): item is PromptGenerationResultItem =>
			typeof item === "object" &&
			item !== null &&
			typeof (item as { query?: unknown }).query === "string" &&
			typeof (item as { queryType?: unknown }).queryType === "string",
	);
}

/**
 * Prompts grouped by framework, in PROMPT_GENERATOR_FRAMEWORKS order, empty
 * groups dropped. Order within a group is the stored order.
 */
export function groupPromptGenerationResults<
	T extends PromptGenerationResultItem,
>(results: readonly T[]): PromptGenerationFrameworkGroup<T>[] {
	const byFramework = new Map<PromptGeneratorFramework, T[]>();
	for (const item of results) {
		const framework = promptGeneratorFrameworkFor(item);
		const list = byFramework.get(framework) ?? [];
		list.push(item);
		byFramework.set(framework, list);
	}
	return PROMPT_GENERATOR_FRAMEWORKS.flatMap((framework) => {
		const prompts = byFramework.get(framework);
		return prompts && prompts.length > 0
			? [
					{
						framework,
						label: PROMPT_GENERATOR_FRAMEWORK_LABELS[framework],
						prompts,
					},
				]
			: [];
	});
}
